import { Badge } from "@simplicity/ui";
import { isPaused } from "./agentSetupModel";
import { agentBadgeText, agentBehindHint, agentImageLine } from "./onsiteModel";
import { agentChangeText } from "./releasesModel";
import type { AgentChange, ChangeAction, OnsiteAgent, OnsiteWorkspace } from "./types";

type BadgeVariant = "success" | "info" | "secondary" | "destructive" | "warning";

const WORKSPACE_STATUS: Record<string, BadgeVariant> = {
  draft: "secondary",
  bootstrapped: "info",
  applied: "success",
  disabled: "warning",
};

const RUN_STATUS: Record<string, BadgeVariant> = {
  pending: "secondary",
  running: "info",
  succeeded: "success",
  failed: "destructive",
};

const AGENT_STATUS: Record<OnsiteAgent["status"], BadgeVariant> = {
  current: "success",
  behind: "warning",
  unknown: "secondary",
};

const AGENT_CHANGE: Record<AgentChange, BadgeVariant> = {
  changed: "info",
  same: "secondary",
  unknown: "warning",
};

const CHANGE_ACTION: Record<ChangeAction, BadgeVariant> = {
  create: "success",
  update: "info",
  read: "secondary",
  replace: "warning",
  delete: "destructive",
};

export function WorkspaceStatusBadge({ status }: { status: string }) {
  return <Badge variant={WORKSPACE_STATUS[status] ?? "secondary"}>{status}</Badge>;
}

export function RunStatusBadge({ status }: { status: string }) {
  return <Badge variant={RUN_STATUS[status] ?? "secondary"}>{status}</Badge>;
}

export function ChangeActionBadge({ action }: { action: ChangeAction }) {
  return <Badge variant={CHANGE_ACTION[action]}>{action}</Badge>;
}

/** Whether a build runs a different agent from the current release; unknown is never "same". */
export function AgentChangeBadge({ change }: { change: AgentChange }) {
  return <Badge variant={AGENT_CHANGE[change]}>{agentChangeText(change)}</Badge>;
}

/** Inherited from the runner Project: a paused client's agent checks in once a day. */
export function PausedBadge({ workspace }: { workspace: Pick<OnsiteWorkspace, "projectStatus"> }) {
  return isPaused(workspace) ? <Badge variant="warning">Paused</Badge> : null;
}

/** Whether the workspace's agent runs the current image; the tooltip says what to do if not. */
export function AgentBadge({ agent }: { agent: OnsiteAgent }) {
  return (
    <Badge
      variant={AGENT_STATUS[agent.status]}
      title={agentBehindHint(agent) ?? agentImageLine(agent)}
    >
      {agentBadgeText(agent)}
    </Badge>
  );
}
